import { RouteObject } from 'react-router-dom';
import { ApprovalView } from '../ApprovalView';
import { ApprovalDashboardRoute } from './ApprovalDashboardRoute';
import { ApprovalMediaRoute } from './ApprovalMediaRoute';
import { SocialMediaSetupRoute } from './SocialMediaSetupRoute';
import { SocialMediaErrorRoute } from './SocialMediaErrorRoute';
import { ClientPlanningRoute } from './ClientPlanningRoute';
import { ClientFeedRoute } from './ClientFeedRoute';
import { PostReviewRoute } from './PostReviewRoute';
import { ClientIdeasRoute } from './ClientIdeasRoute';
import { ClientBriefRoute } from './ClientBriefRoute';
import { ApprovalReportRoute } from './ApprovalReportRoute';
import { PortalPlanRoute } from './PortalPlanRoute';
import { portalAuthLoader } from '../utils/portalAuthLoader';
import { MagicLinkAuth } from '../pages/MagicLinkAuth';
import { TokenExpiredScreen } from '../pages/TokenExpiredScreen';
import { UnauthorizedScreen } from '../pages/UnauthorizedScreen';
import { PortalStartRoute } from './PortalStartRoute';
import { ClientBioRoute } from './ClientBioRoute';
import { ClientTasksRoute } from './ClientTasksRoute';

export const createPortalRoutes = (): RouteObject[] => [
  // PWA launch target (manifest start_url): resolve to the stored workspace
  // or the portal login page.
  {
    path: '/portal/start',
    element: <PortalStartRoute />,
  },
  // Magic link authentication route (standalone, not nested in ApprovalView).
  // Both client-portal and creator-portal magic links exchange here; the
  // post-exchange destination is driven by the `redirect` query param.
  {
    path: '/collaborators/:clientId/auth',
    element: <MagicLinkAuth />,
  },
  // Backwards-compat alias for magic links minted before the rename to
  // `/collaborators/:clientId/auth`. Drop 24h after the rename ships,
  // once all in-flight tokens have expired.
  {
    path: '/client/:clientId/auth',
    element: <MagicLinkAuth />,
  },
  // Token expired error screen
  {
    path: '/client/:clientId/expired',
    element: <TokenExpiredScreen />,
  },
  // Unauthorized error screen
  {
    path: '/client/:clientId/unauthorized',
    element: <UnauthorizedScreen />,
  },
  {
    path: '/client/:clientId',
    // No loader on parent - allows login form to show
    // Child routes have loaders to protect content
    element: <ApprovalView />,
    children: [
      {
        index: true,
        // Dashboard can show login form OR content, handled by ApprovalView
        element: <ApprovalDashboardRoute />,
      },
      {
        // Client photo approval view (relocated from /foto)
        path: 'review/media',
        loader: portalAuthLoader,
        element: <ApprovalMediaRoute />,
      },
      {
        path: 'reel',
        loader: portalAuthLoader,
        element: <ApprovalMediaRoute />,
      },
      {
        path: 'posts',
        loader: portalAuthLoader,
        element: <PostReviewRoute />,
      },
      {
        path: 'planning',
        loader: portalAuthLoader,
        element: <ClientPlanningRoute />,
      },
      {
        path: 'planning/feed/:postIndex',
        loader: portalAuthLoader,
        element: <ClientFeedRoute />,
      },
      {
        path: 'ideas',
        loader: portalAuthLoader,
        element: <ClientIdeasRoute />,
      },
      {
        path: 'briefs/:briefId',
        loader: portalAuthLoader,
        element: <ClientBriefRoute />,
      },
      {
        path: 'social-media',
        loader: portalAuthLoader,
        element: <SocialMediaSetupRoute />,
      },
      {
        path: 'social-media-error',
        element: <SocialMediaErrorRoute />,
      },
      {
        path: 'report',
        loader: portalAuthLoader,
        element: <ApprovalReportRoute />,
      },
      {
        // The client's own plan and agreed prices (finance-pricing target)
        path: 'piano',
        loader: portalAuthLoader,
        element: <PortalPlanRoute />,
      },
      {
        // Reached from the burger menu and the dashboard (DD-053): the client's tasks.
        path: 'tasks',
        loader: portalAuthLoader,
        element: <ClientTasksRoute />,
      },
      {
        // Reached from the dashboard's Dettagli card: per-platform profile copy.
        path: 'bio',
        loader: portalAuthLoader,
        element: <ClientBioRoute />,
      },
    ],
  },
];
